// frontend/src/components/portal/LogoUploader.js
//
// Tenant-facing receipt/brand logo uploader, for the Appearance section of the
// web Portal's Settings page. Not the native app: uploading and cropping is a
// desktop task.
//
// Three decisions worth knowing before editing this file.
//
// 1. NO CROPPER LIBRARY. package.json carries no image or cropping dependency,
//    and the two obvious candidates are 15-30kB gzipped for a modal a tenant
//    opens roughly once. The canvas work has to exist anyway for the 1-bit
//    preview, so the crop rectangle rides on it.
//
// 2. NO ASPECT LOCK. The printer imposes a HEIGHT ceiling with the width free
//    (see thermalLogo.js). Locking the frame to 4:1 would crop a square mark
//    into a letterbox and destroy it, for the same 12mm of paper either way.
//
// 3. THE PREVIEW IS THE REAL RENDER. Composite onto white, scale, hard
//    threshold at 128 — the exact pipeline logo_raster.py uses. A logo that
//    looks fine in colour can turn to mush at 1 bit, and this is the only
//    place a tenant will ever find that out before a customer does.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { tenantService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { C } from '../../styles/theme';
import {
    MAX_HEIGHT_DOTS,
    describeCrop,
    dotsToMm,
    exportCrop,
    renderThermal,
} from '../../utils/thermalLogo';

const FONT = "'Plus Jakarta Sans', sans-serif";

// The crop stage. Fixed pixel size so the drag maths has one coordinate space
// to worry about; the source image is letterboxed into it.
const STAGE_WIDTH = 460;
const STAGE_HEIGHT = 300;
const HANDLE = 14;

// The crop rectangle is held in SOURCE pixels, not stage pixels, and the stage
// is only a viewport onto it. Holding it in stage pixels loses a pixel or two
// on the round trip back — enough to turn the reference logo's "12.0mm" into
// "11.9mm", which makes a liar of the one number this whole modal exists to
// show. MIN_RECT is therefore in source pixels too.
const MIN_RECT = 16;

// 80mm paper at 203dpi is 640 dots. The preview sits on a strip this wide so
// "12mm" is something a tenant can see rather than only read.
const PAPER_DOTS = 640;

const label = {
    fontFamily: FONT, fontSize: 11, fontWeight: 700,
    letterSpacing: 0.8, textTransform: 'uppercase',
    color: C.muted, display: 'block', marginBottom: 6,
};

const IconImage = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" />
    </svg>
);

const IconWarning = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const primaryButton = (disabled) => ({
    padding: '9px 22px', borderRadius: 9, border: 'none',
    background: C.green, color: C.white,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: FONT, fontSize: 13, fontWeight: 700,
    opacity: disabled ? 0.45 : 1, transition: 'opacity 0.15s',
});

const secondaryButton = {
    padding: '9px 18px', borderRadius: 9, border: `1.5px solid ${C.border}`,
    background: C.bg, cursor: 'pointer',
    fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.muted,
};

/** Letterbox a source into the stage and return the mapping between them. */
function stageFit(sourceWidth, sourceHeight) {
    const scale = Math.min(STAGE_WIDTH / sourceWidth, STAGE_HEIGHT / sourceHeight, 1);
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    return {
        scale,
        width,
        height,
        left: Math.round((STAGE_WIDTH - width) / 2),
        top: Math.round((STAGE_HEIGHT - height) / 2),
    };
}

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

// ─── The crop stage ───────────────────────────────────────────────────────
// Module scope, not nested in LogoUploader: a component declared inside a
// render gets a new function identity every time, so React unmounts and
// remounts the subtree — which here would drop the pointer capture mid-drag.
const CropStage = ({ src, source, fit, rect, setRect }) => {
    const dragRef = useRef(null);

    // Source pixels → stage pixels, for drawing only.
    const framed = {
        left: fit.left + rect.x * fit.scale,
        top: fit.top + rect.y * fit.scale,
        width: rect.width * fit.scale,
        height: rect.height * fit.scale,
    };

    const onPointerDown = (mode) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragRef.current = { mode, startX: event.clientX, startY: event.clientY, rect: { ...rect } };
        if (event.target.setPointerCapture && event.pointerId !== undefined) {
            event.target.setPointerCapture(event.pointerId);
        }
    };

    const onPointerMove = (event) => {
        const drag = dragRef.current;
        if (!drag) return;
        // Stage pixels back to source pixels before anything is decided.
        const dx = (event.clientX - drag.startX) / fit.scale;
        const dy = (event.clientY - drag.startY) / fit.scale;
        setRect(nextRect(drag, dx, dy, source));
    };

    const endDrag = () => { dragRef.current = null; };

    const handleStyle = (corner) => ({
        position: 'absolute', width: HANDLE, height: HANDLE,
        background: C.white, border: `2px solid ${C.green}`, borderRadius: 3,
        cursor: corner === 'nw' || corner === 'se' ? 'nwse-resize' : 'nesw-resize',
        touchAction: 'none',
        left: corner[1] === 'w' ? -HANDLE / 2 : undefined,
        right: corner[1] === 'e' ? -HANDLE / 2 : undefined,
        top: corner[0] === 'n' ? -HANDLE / 2 : undefined,
        bottom: corner[0] === 's' ? -HANDLE / 2 : undefined,
    });

    return (
        <div
            data-testid="logo-crop-stage"
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerLeave={endDrag}
            style={{
                position: 'relative', width: STAGE_WIDTH, height: STAGE_HEIGHT,
                background: C.faint, borderRadius: 10,
                border: `1.5px solid ${C.border}`, overflow: 'hidden',
                touchAction: 'none', userSelect: 'none',
            }}
        >
            <img
                src={src}
                alt=""
                draggable={false}
                style={{
                    position: 'absolute', left: fit.left, top: fit.top,
                    width: fit.width, height: fit.height, pointerEvents: 'none',
                }}
            />
            {/* Scrim everywhere except the frame, so the crop reads as the
                part that survives rather than as a box drawn on top. */}
            <div style={{
                position: 'absolute',
                left: framed.left, top: framed.top,
                width: framed.width, height: framed.height,
                pointerEvents: 'none',
                boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)',
            }} />
            <div
                data-testid="logo-crop-frame"
                onPointerDown={onPointerDown('move')}
                style={{
                    position: 'absolute',
                    left: framed.left, top: framed.top,
                    width: framed.width, height: framed.height,
                    border: `2px solid ${C.green}`, cursor: 'move',
                    boxSizing: 'border-box', touchAction: 'none',
                }}
            >
                {['nw', 'ne', 'sw', 'se'].map(corner => (
                    <div key={corner} onPointerDown={onPointerDown(corner)}
                        style={handleStyle(corner)} />
                ))}
            </div>
        </div>
    );
};

/**
 * Where a drag leaves the crop rectangle. All arguments in SOURCE pixels.
 *
 * Clamped to the image on every path: a crop that runs off the edge would ask
 * drawImage for pixels that do not exist, which silently yields transparent
 * ones — and transparency composites to white, so the tenant would get an
 * invisible margin baked into their logo.
 */
export function nextRect(drag, dx, dy, source) {
    const { mode, rect } = drag;
    const maxX = source.width;
    const maxY = source.height;

    if (mode === 'move') {
        return {
            ...rect,
            x: Math.round(clamp(rect.x + dx, 0, maxX - rect.width)),
            y: Math.round(clamp(rect.y + dy, 0, maxY - rect.height)),
        };
    }

    let { x, y, width, height } = rect;
    if (mode[1] === 'w') {
        const nx = clamp(x + dx, 0, x + width - MIN_RECT);
        width += x - nx;
        x = nx;
    } else {
        width = clamp(width + dx, MIN_RECT, maxX - x);
    }
    if (mode[0] === 'n') {
        const ny = clamp(y + dy, 0, y + height - MIN_RECT);
        height += y - ny;
        y = ny;
    } else {
        height = clamp(height + dy, MIN_RECT, maxY - y);
    }
    return {
        x: Math.round(x), y: Math.round(y),
        width: Math.round(width), height: Math.round(height),
    };
}

// ─── The 1-bit preview ────────────────────────────────────────────────────
const ThermalPreview = ({ image, crop, fitted }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current || !image) return;
        renderThermal(canvasRef.current, image, crop, fitted);
    }, [image, crop, fitted]);

    return (
        <div style={{
            background: '#ffffff', borderRadius: 6,
            border: `1.5px solid ${C.border}`,
            padding: 10, width: PAPER_DOTS / 2, boxSizing: 'border-box',
            display: 'flex', justifyContent: 'center',
        }}>
            <canvas
                ref={canvasRef}
                data-testid="logo-thermal-preview"
                style={{
                    width: fitted.width, height: fitted.height,
                    imageRendering: 'pixelated',
                }}
            />
        </div>
    );
};

// ─── The uploader ─────────────────────────────────────────────────────────
const LogoUploader = ({ config, onToast, onSaved }) => {
    const { t } = useLanguage();
    const { refreshTenantConfig } = useAuth();

    const currentLogo = (config?.receipt_logo || config?.brand_logo || '').trim() || null;
    const [loadFailed, setLoadFailed] = useState(false);
    const [open, setOpen] = useState(false);

    const [image, setImage] = useState(null);       // HTMLImageElement
    const [objectUrl, setObjectUrl] = useState(null);
    const [rect, setRect] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [readError, setReadError] = useState(null);

    // A fresh URL is a fresh chance for the <img> to succeed. Without this a
    // tenant who fixes a broken logo keeps seeing the failure notice.
    useEffect(() => { setLoadFailed(false); }, [currentLogo]);

    // Which file selection is current. "Wrong logo, let me pick another" is an
    // ordinary thing to do, and without a token a slow first decode can land
    // after a fast second one and silently put the wrong image under the frame.
    const selectionRef = useRef(0);

    const releaseObjectUrl = useCallback(() => {
        setObjectUrl(previous => {
            if (previous && URL && URL.revokeObjectURL) {
                URL.revokeObjectURL(previous);
            }
            return null;
        });
    }, []);

    const close = useCallback(() => {
        selectionRef.current += 1;
        setOpen(false);
        setImage(null);
        setRect(null);
        setReadError(null);
        releaseObjectUrl();
    }, [releaseObjectUrl]);

    // A modal unmounted mid-selection — a route change, a logout — still holds
    // a blob. The browser keeps it for the life of the tab otherwise.
    useEffect(() => () => { selectionRef.current += 1; }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => { if (event.key === 'Escape') close(); };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, close]);

    const onFile = (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;
        setReadError(null);

        // The previous selection's blob is dead the moment a new file is
        // chosen. Only close() used to release one, so every re-pick before
        // closing leaked one for the life of the tab.
        releaseObjectUrl();

        const selection = (selectionRef.current += 1);
        const url = URL.createObjectURL(file);
        setObjectUrl(url);

        const loaded = new Image();
        loaded.onload = () => {
            if (selectionRef.current !== selection) return;
            setImage(loaded);
            // Default to the whole image. Most tenant logos are already the
            // right shape, and a modal that demands a crop before it will do
            // anything is a modal that has invented work for its user.
            setRect({
                x: 0, y: 0,
                width: loaded.naturalWidth, height: loaded.naturalHeight,
            });
        };
        loaded.onerror = () => {
            if (selectionRef.current !== selection) return;
            setReadError('logo_file_unreadable');
        };
        loaded.src = url;
    };

    const source = image
        ? { width: image.naturalWidth, height: image.naturalHeight }
        : null;
    const fit = source ? stageFit(source.width, source.height) : null;
    const summary = rect ? describeCrop(rect.width, rect.height) : null;
    const blocked = Boolean(summary && summary.rejection);

    const confirm = async () => {
        if (!image || !rect || blocked || uploading) return;
        setUploading(true);
        try {
            const blob = await exportCrop(image, rect);
            await tenantService.uploadLogo(blob);
            // TWO refreshes, and both are needed. refreshTenantConfig updates
            // AuthContext, which is what the portal shell and the native
            // Settings screen read. onSaved refetches the SETTINGS PAGE's own
            // copy of the config — the one the preview a few pixels above this
            // modal is rendered from. Without it the tenant is told the upload
            // worked while still looking at the old logo.
            if (refreshTenantConfig) await refreshTenantConfig();
            if (onSaved) await onSaved();
            // NOT 'logo_uploaded' — that key already exists and means
            // "Uploaded" as a status word on the native settings screen.
            onToast('success', t('logo_saved', {}, 'Logo updated'));
            close();
        } catch (error) {
            const detail = error?.response?.data?.detail
                || t('logo_upload_failed', {}, 'The logo could not be uploaded.');
            onToast('error', String(detail));
        } finally {
            setUploading(false);
        }
    };

    const guidance = t(
        'logo_guidance',
        { mm: Math.round(dotsToMm(MAX_HEIGHT_DOTS)) },
        'Your logo prints at the top of every receipt, up to {mm}mm tall — about four lines of text. Any shape works: a wide mark prints wider, a square one prints smaller. It must not be taller than it is wide.',
    );

    return (
        <div>
            <label style={label}>{t('receipt_and_brand_logo', {}, 'Receipt & Brand Logo')}</label>

            <div style={{
                background: C.white, border: `1.5px solid ${C.border}`,
                borderRadius: 12, padding: 20, maxWidth: 560,
            }}>
                {currentLogo && !loadFailed && (
                    <div style={{
                        background: '#ffffff', border: `1px solid ${C.borderLight}`,
                        borderRadius: 8, padding: 14, marginBottom: 14,
                        display: 'flex', justifyContent: 'center',
                    }}>
                        <img
                            data-testid="logo-current"
                            src={currentLogo}
                            alt={t('current_logo_alt', {}, 'Your current logo')}
                            onError={() => setLoadFailed(true)}
                            style={{ maxWidth: '100%', maxHeight: 90 }}
                        />
                    </div>
                )}

                {currentLogo && loadFailed && (
                    // Item 11. Every surface in the product used to hide this
                    // with onerror, so a tenant whose branding broke at the
                    // rebrand had no way to tell broken from absent.
                    <div
                        data-testid="logo-load-failed"
                        role="alert"
                        style={{
                            background: C.redLight, border: `1.5px solid ${C.redBorder}`,
                            borderRadius: 8, padding: 14, marginBottom: 14,
                            display: 'flex', gap: 10, alignItems: 'flex-start',
                            color: C.redText || C.red,
                        }}
                    >
                        <span style={{ flexShrink: 0, marginTop: 1 }}><IconWarning /></span>
                        <div>
                            <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700 }}>
                                {t('logo_failed_to_load', {}, 'Your logo could not be loaded, so it is not printing on receipts.')}
                            </div>
                            <div style={{
                                fontFamily: 'monospace', fontSize: 11, marginTop: 6,
                                wordBreak: 'break-all', opacity: 0.85,
                            }}>
                                {currentLogo}
                            </div>
                            <div style={{ fontFamily: FONT, fontSize: 12, marginTop: 6 }}>
                                {t('logo_failed_to_load_fix', {}, 'Upload it again to fix this.')}
                            </div>
                        </div>
                    </div>
                )}

                {!currentLogo && (
                    <div
                        data-testid="logo-empty"
                        style={{
                            textAlign: 'center', padding: '18px 10px 22px', color: C.mutedLight,
                        }}
                    >
                        <IconImage />
                        <div style={{
                            fontFamily: FONT, fontSize: 13, fontWeight: 600,
                            color: C.muted, marginTop: 8,
                        }}>
                            {t('no_logo_yet', {}, 'No logo yet')}
                        </div>
                        <div style={{ fontFamily: FONT, fontSize: 12, marginTop: 4 }}>
                            {t('no_logo_yet_description', {}, 'Receipts print without one until you add it.')}
                        </div>
                    </div>
                )}

                <button
                    data-testid="logo-open-uploader"
                    onClick={() => setOpen(true)}
                    style={primaryButton(false)}
                >
                    {currentLogo
                        ? t('replace_logo', {}, 'Replace Logo')
                        : t('upload_logo', {}, 'Upload Logo')}
                </button>
            </div>

            {open && (
                <div
                    data-testid="logo-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('upload_logo', {}, 'Upload Logo')}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 1000,
                        background: 'rgba(15, 23, 42, 0.5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 20,
                    }}
                    onClick={(event) => { if (event.target === event.currentTarget) close(); }}
                >
                    <div style={{
                        background: C.surface || C.white, borderRadius: 14,
                        padding: 24, maxWidth: 940, width: '100%',
                        maxHeight: '92vh', overflowY: 'auto',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
                    }}>
                        <div style={{
                            display: 'flex', alignItems: 'flex-start',
                            justifyContent: 'space-between', gap: 16, marginBottom: 16,
                        }}>
                            <div>
                                <h3 style={{
                                    fontFamily: FONT, fontSize: 18, fontWeight: 800,
                                    color: C.text, margin: 0,
                                }}>
                                    {t('upload_logo', {}, 'Upload Logo')}
                                </h3>
                                <p
                                    data-testid="logo-guidance"
                                    style={{
                                        fontFamily: FONT, fontSize: 12.5, color: C.muted,
                                        margin: '6px 0 0', lineHeight: 1.55, maxWidth: 560,
                                    }}
                                >
                                    {guidance}
                                </p>
                            </div>
                            <button
                                data-testid="logo-close"
                                onClick={close}
                                aria-label={t('close', {}, 'Close')}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: C.mutedLight, fontSize: 26, lineHeight: 1,
                                    padding: '0 4px', flexShrink: 0,
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <input
                            data-testid="logo-file-input"
                            type="file"
                            accept="image/png,image/jpeg"
                            onChange={onFile}
                            style={{ fontFamily: FONT, fontSize: 12.5, marginBottom: 16 }}
                        />

                        {readError && (
                            <div style={{
                                fontFamily: FONT, fontSize: 12.5, color: C.red, marginBottom: 12,
                            }}>
                                {t('logo_file_unreadable', {}, 'That file could not be read as an image. Save it as a PNG or JPEG and try again.')}
                            </div>
                        )}

                        {image && rect && fit && (
                            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                                <div>
                                    <label style={label}>{t('choose_what_prints', {}, 'Choose what prints')}</label>
                                    <CropStage src={objectUrl} source={source} fit={fit} rect={rect} setRect={setRect} />
                                </div>

                                <div style={{ minWidth: 300, flex: 1 }}>
                                    <label style={label}>{t('how_it_will_print', {}, 'How it will print')}</label>
                                    <ThermalPreview
                                        image={image}
                                        crop={rect}
                                        fitted={summary.fitted}
                                    />

                                    <div
                                        data-testid="logo-paper-cost"
                                        style={{
                                            fontFamily: FONT, fontSize: 13, fontWeight: 600,
                                            color: C.text, marginTop: 14, lineHeight: 1.5,
                                        }}
                                    >
                                        {t(
                                            'logo_paper_cost',
                                            {
                                                height: summary.heightMm.toFixed(1),
                                                width: summary.widthMm.toFixed(1),
                                            },
                                            'Uses {height}mm at the top of every receipt, {width}mm wide.',
                                        )}
                                    </div>

                                    {blocked && (
                                        <div
                                            data-testid="logo-blocked"
                                            role="alert"
                                            style={{
                                                background: C.redLight,
                                                border: `1.5px solid ${C.redBorder}`,
                                                borderRadius: 8, padding: 12, marginTop: 12,
                                                fontFamily: FONT, fontSize: 12.5,
                                                color: C.redText || C.red, lineHeight: 1.5,
                                            }}
                                        >
                                            {t(
                                                summary.rejection,
                                                { mm: Math.round(dotsToMm(MAX_HEIGHT_DOTS)) },
                                                'This crop is taller than it is wide, so at {mm}mm tall it would print too narrow to read. Drag the frame wider or shorter.',
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div style={{
                            display: 'flex', gap: 10, marginTop: 22,
                            justifyContent: 'flex-end',
                        }}>
                            <button onClick={close} style={secondaryButton}>
                                {t('cancel', {}, 'Cancel')}
                            </button>
                            <button
                                data-testid="logo-confirm"
                                onClick={confirm}
                                disabled={!image || blocked || uploading}
                                style={primaryButton(!image || blocked || uploading)}
                            >
                                {uploading
                                    ? t('uploading', {}, 'Uploading...')
                                    : t('save_logo', {}, 'Save Logo')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LogoUploader;
